import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useAuth } from "@/hooks/use-auth";
import { Link } from "wouter";

const loginSchema = z.object({
  email: z.string().min(1, "Email is required"),
  password: z.string().min(1, "Password is required"),
});

const registerSchema = z.object({
  fullName: z.string().min(1, "Full name is required"),
  email: z.string().email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

type LoginFormValues = z.infer<typeof loginSchema>;
type RegisterFormValues = z.infer<typeof registerSchema> & { type: "artist" };

export function AuthForm() {
  const { loginMutation, registerMutation } = useAuth();
  const [isLogin, setIsLogin] = useState(true);

  const loginForm = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  const registerForm = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: { fullName: "", email: "", password: "" },
  });

  const currentForm = isLogin ? loginForm : registerForm;

  const handleSubmit = (data: any) => {
    if (isLogin) {
      loginMutation.mutate(data);
    } else {
      registerMutation.mutate({ ...data, type: "artist" });
    }
  };

  const isPending = loginMutation.isPending || registerMutation.isPending;

  return (
    <div className="w-full">
      <form onSubmit={currentForm.handleSubmit(handleSubmit)}>
        <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] overflow-hidden">
          {/* Full Name (registration only) */}
          {!isLogin && (
            <>
              <div className="relative">
                <input
                  id="fullName"
                  type="text"
                  placeholder="Full Name"
                  className="w-full bg-transparent px-4 py-3.5 text-[18px] text-white placeholder:text-white/30 outline-none"
                  {...registerForm.register("fullName")}
                />
              </div>
              <div className="border-t border-white/[0.06]" />
            </>
          )}

          {/* Email */}
          <div className="relative">
            <input
              id="email"
              type="text"
              placeholder="Email"
              className="w-full bg-transparent px-4 py-3.5 text-[18px] text-white placeholder:text-white/30 outline-none"
              {...currentForm.register("email")}
            />
          </div>

          <div className="border-t border-white/[0.06]" />

          {/* Password */}
          <div className="relative">
            <input
              id="password"
              type="password"
              placeholder="Password"
              className="w-full bg-transparent px-4 py-3.5 text-[18px] text-white placeholder:text-white/30 outline-none"
              {...currentForm.register("password")}
            />
          </div>
        </div>

        {/* Errors */}
        {(currentForm.formState.errors.email || currentForm.formState.errors.password || (!isLogin && registerForm.formState.errors.fullName)) && (
          <div className="mt-2 px-1">
            {Object.values(currentForm.formState.errors).map((error, i) => (
              <p key={i} className="text-[14px] text-red-400">{error.message}</p>
            ))}
          </div>
        )}

        {/* Forgot password */}
        {isLogin && (
          <div className="mt-3 px-1">
            <Link href="/reset-password">
              <a className="text-[14px] text-white/40 hover:text-white/60 transition-colors">
                Forgot password?
              </a>
            </Link>
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={isPending}
          className="w-full mt-6 py-3 rounded-xl bg-white text-black text-[18px] font-medium hover:bg-white/90 active:bg-white/80 transition-colors disabled:opacity-40"
        >
          {isLogin
            ? (isPending ? "Signing in..." : "Sign In")
            : (isPending ? "Creating account..." : "Create Account")
          }
        </button>
      </form>

      {/* Toggle */}
      <p className="text-center mt-6 text-[14px] text-white/40">
        {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
        <button
          type="button"
          onClick={() => setIsLogin(!isLogin)}
          className="text-white/70 hover:text-white transition-colors"
        >
          {isLogin ? "Sign Up" : "Sign In"}
        </button>
      </p>
    </div>
  );
}
